// Data Loader for various file formats

class DataLoader {
  constructor() {
    this.loaders = {
      stl: new THREE.STLLoader(),
      ply: new THREE.PLYLoader(),
      vtk: new THREE.VTKLoader(),
      obj: new THREE.OBJLoader()
    };
  }

  // Load file based on extension
  async loadFile(file) {
    const extension = file.name.split('.').pop().toLowerCase();

    switch(extension) {
      case 'stl':
        return await this.loadSTL(file);
      case 'obj':
        return await this.loadOBJ(file);
      case 'ply':
        return await this.loadPLY(file);
      case 'vtk':
      case 'vtu':
      case 'vtp':
        return await this.loadVTK(file);
      case 'json':
        return await this.loadJSON(file);
      case 'case':
        // EnSight case file - needs server-side processing
        return { type: 'ensight', file: file, needsUpload: true };
      case 'cas':
      case 'dat':
      case 'msh':
        // Fluent case file - needs server-side processing
        return { type: 'fluent', file: file, needsUpload: true };
      case 'geo':
        // EnSight geometry file - needs server-side processing
        return { type: 'ensight', file: file, needsUpload: true };
      case 'step':
      case 'stp':
        return await this.loadSTEP(file);
      default:
        throw new Error(`Unsupported file format: ${extension}`);
    }
  }

  // Load STL file
  async loadSTL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const geometry = this.loaders.stl.parse(e.target.result);
          geometry.computeVertexNormals();

          const data = {
            type: 'mesh',
            geometry: geometry,
            vertices: this.extractVertices(geometry),
            metadata: {
              format: 'STL',
              fileName: file.name,
              fileSize: file.size,
              vertexCount: geometry.attributes.position.count,
              faceCount: geometry.attributes.position.count / 3
            }
          };

          resolve(data);
        } catch(err) {
          reject(new Error(`Failed to parse STL: ${err.message}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  // Load OBJ file
  async loadOBJ(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const object = this.loaders.obj.parse(e.target.result);

          // OBJLoader returns a Group containing meshes
          // Collect child meshes with their names
          const childMeshes = [];
          let totalVertices = 0;
          let totalFaces = 0;

          object.traverse((child) => {
            if (child.isMesh && child.geometry) {
              child.geometry.computeVertexNormals();
              const vertexCount = child.geometry.attributes.position.count;
              const faceCount = child.geometry.index
                ? child.geometry.index.count / 3
                : vertexCount / 3;
              totalVertices += vertexCount;
              totalFaces += faceCount;
              childMeshes.push({
                name: child.name || `Part_${childMeshes.length + 1}`,
                geometry: child.geometry,
                vertexCount: vertexCount,
                faceCount: faceCount
              });
            }
          });

          if (childMeshes.length === 0) {
            throw new Error('No geometry found in OBJ file');
          }

          // Single group: return as simple mesh (no part splitting needed)
          if (childMeshes.length === 1) {
            const data = {
              type: 'mesh',
              geometry: childMeshes[0].geometry,
              vertices: this.extractVertices(childMeshes[0].geometry),
              metadata: {
                format: 'OBJ',
                fileName: file.name,
                fileSize: file.size,
                vertexCount: totalVertices,
                faceCount: totalFaces
              }
            };
            resolve(data);
            return;
          }

          // Multiple groups: preserve as separate parts for per-part control
          const parts = [];
          const boundaries = {};

          // Deduplicate names (OBJ files can have repeated group names)
          const nameCount = {};
          childMeshes.forEach(cm => {
            nameCount[cm.name] = (nameCount[cm.name] || 0) + 1;
          });
          const nameIndex = {};

          childMeshes.forEach(cm => {
            let partName = cm.name;
            if (nameCount[partName] > 1) {
              nameIndex[partName] = (nameIndex[partName] || 0) + 1;
              partName = `${partName}_${nameIndex[partName]}`;
            }
            parts.push({
              name: partName,
              geometry: cm.geometry,
              vertexCount: cm.vertexCount,
              faceCount: cm.faceCount
            });
            boundaries[partName] = { nFaces: cm.faceCount };
          });

          const data = {
            type: 'mesh',
            parts: parts,
            boundaries: boundaries,
            metadata: {
              format: 'OBJ',
              fileName: file.name,
              fileSize: file.size,
              vertexCount: totalVertices,
              faceCount: totalFaces,
              partCount: parts.length
            }
          };

          resolve(data);
        } catch(err) {
          reject(new Error(`Failed to parse OBJ: ${err.message}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  // Load STEP/STP file using occt-import-js (OpenCASCADE WASM)
  async loadSTEP(file) {
    // Initialize OCCT WASM module (cached after first call)
    if (!this._occtPromise) {
      if (typeof occtimportjs === 'undefined') {
        throw new Error('occt-import-js library not loaded');
      }
      this._occtPromise = occtimportjs();
    }
    const occt = await this._occtPromise;

    const buffer = await file.arrayBuffer();
    const fileBuffer = new Uint8Array(buffer);

    // Configure tessellation for better quality (prevents missing parts)
    const config = {
      linearDeflection: 0.1,      // Lower = finer mesh (default 0.1)
      angularDeflection: 0.5,     // Lower = more accurate (default 0.5)
      parallelProcessing: true    // Use Web Workers if available
    };

    const result = occt.ReadStepFile(fileBuffer, config);
    if (!result.success) {
      throw new Error('Failed to parse STEP file');
    }

    if (!result.meshes || result.meshes.length === 0) {
      throw new Error('No geometry found in STEP file');
    }

    let totalVertices = 0;
    let totalFaces = 0;

    // Build name mapping from the assembly tree
    const meshNames = {};
    const collectNames = (node) => {
      if (node.meshes) {
        node.meshes.forEach(idx => {
          meshNames[idx] = node.name || result.meshes[idx]?.name || null;
        });
      }
      if (node.children) {
        node.children.forEach(collectNames);
      }
    };
    if (result.root) collectNames(result.root);

    // Convert each OCCT mesh to a Three.js BufferGeometry
    const parts = [];
    const boundaries = {};
    const usedNames = {};

    result.meshes.forEach((mesh, idx) => {
      const posArray = mesh.attributes.position.array;
      const idxArray = mesh.index.array;
      const vertexCount = posArray.length / 3;
      const faceCount = idxArray.length / 3;
      totalVertices += vertexCount;
      totalFaces += faceCount;

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position',
        new THREE.Float32BufferAttribute(new Float32Array(posArray), 3));
      geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(idxArray), 1));

      // Use normals from OCCT if available, otherwise compute
      if (mesh.attributes.normal && mesh.attributes.normal.array) {
        geometry.setAttribute('normal',
          new THREE.Float32BufferAttribute(new Float32Array(mesh.attributes.normal.array), 3));
      } else {
        geometry.computeVertexNormals();
      }

      // Determine part name
      let baseName = meshNames[idx] || mesh.name || `Part_${idx + 1}`;
      // Deduplicate
      if (usedNames[baseName] != null) {
        usedNames[baseName]++;
        baseName = `${baseName}_${usedNames[baseName]}`;
      } else {
        usedNames[baseName] = 0;
      }

      // Extract color from OCCT mesh if present
      let partColor = null;
      if (mesh.color && mesh.color.length >= 3) {
        // OCCT colors are in 0-1 range
        partColor = [mesh.color[0], mesh.color[1], mesh.color[2]];
      }

      parts.push({
        name: baseName,
        geometry: geometry,
        color: partColor,
        vertexCount: vertexCount,
        faceCount: faceCount
      });
      boundaries[baseName] = { nFaces: faceCount };
    });

    // Single part: return simple mesh
    if (parts.length === 1) {
      const data = {
        type: 'mesh',
        geometry: parts[0].geometry,
        vertices: this.extractVertices(parts[0].geometry),
        metadata: {
          format: 'STEP',
          fileName: file.name,
          fileSize: file.size,
          vertexCount: totalVertices,
          faceCount: totalFaces
        }
      };
      return data;
    }

    // Multiple parts: return with assembly structure for per-part control
    return {
      type: 'mesh',
      parts: parts,
      boundaries: boundaries,
      metadata: {
        format: 'STEP',
        fileName: file.name,
        fileSize: file.size,
        vertexCount: totalVertices,
        faceCount: totalFaces,
        partCount: parts.length
      }
    };
  }

  // Load PLY file
  async loadPLY(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const geometry = this.loaders.ply.parse(e.target.result);
          geometry.computeVertexNormals();

          const data = {
            type: 'mesh',
            geometry: geometry,
            vertices: this.extractVertices(geometry),
            colors: this.extractColors(geometry),
            metadata: {
              format: 'PLY',
              fileName: file.name,
              fileSize: file.size,
              vertexCount: geometry.attributes.position.count
            }
          };

          resolve(data);
        } catch(err) {
          reject(new Error(`Failed to parse PLY: ${err.message}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  // Load VTK file
  async loadVTK(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const geometry = this.loaders.vtk.parse(e.target.result);
          geometry.computeVertexNormals();

          const data = {
            type: 'mesh',
            geometry: geometry,
            vertices: this.extractVertices(geometry),
            metadata: {
              format: 'VTK',
              fileName: file.name,
              fileSize: file.size,
              vertexCount: geometry.attributes.position.count
            }
          };

          resolve(data);
        } catch(err) {
          reject(new Error(`Failed to parse VTK: ${err.message}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  // Load JSON file (custom format for simulation data)
  async loadJSON(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target.result);

          // Detect JSON format
          if (json.type === 'simulation-data') {
            resolve(this.parseSimulationData(json, file));
          } else if (json.vertices && json.faces) {
            resolve(this.parseMeshJSON(json, file));
          } else if (json.points) {
            resolve(this.parsePointsJSON(json, file));
          } else {
            reject(new Error('Unknown JSON format'));
          }
        } catch(err) {
          reject(new Error(`Failed to parse JSON: ${err.message}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  // Parse simulation data format
  parseSimulationData(json, file) {
    const { grid, metadata } = json;

    const rawTimeSeries = Array.isArray(json.time_series)
      ? json.time_series
      : Array.isArray(json.timeSeries)
        ? json.timeSeries
        : [];

    const timeSeries = rawTimeSeries.map(frame => {
      const timeValue = typeof frame.time === 'number'
        ? frame.time
        : parseFloat(frame.time);
      return {
        time: Number.isFinite(timeValue) ? timeValue : 0,
        fields: frame.fields || {}
      };
    });

    let times = Array.isArray(json.times)
      ? json.times
      : Array.isArray(json.timesteps)
        ? json.timesteps
        : [];

    if ((!times || times.length === 0) && timeSeries.length > 0) {
      times = timeSeries.map(frame => frame.time);
    }

    const defaultFields = json.fields || {};
    let currentIndex = typeof json.currentTimestep === 'number'
      ? json.currentTimestep
      : (timeSeries.length > 0 ? timeSeries.length - 1 : 0);

    if (timeSeries.length > 0) {
      currentIndex = Math.min(Math.max(currentIndex, 0), timeSeries.length - 1);
    } else {
      currentIndex = 0;
    }

    const currentFields = timeSeries.length > 0
      ? (timeSeries[currentIndex].fields || {})
      : defaultFields;

    return {
      type: 'simulation',
      grid: grid,
      fields: currentFields,
      timeSeries: timeSeries,
      times: times,
      timesteps: times && times.length > 0 ? times : [0],
      currentTimestep: currentIndex,
      available_fields: json.available_fields || Object.keys(currentFields),
      metadata: {
        format: 'Simulation Data',
        fileName: file.name,
        fileSize: file.size,
        gridSize: grid ? [grid.nx, grid.ny, grid.nz] : [0, 0, 0],
        ...metadata
      }
    };
  }

  // Parse mesh JSON format
  parseMeshJSON(json, file) {
    const geometry = new THREE.BufferGeometry();

    // Add vertices
    const vertices = new Float32Array(json.vertices.flat());
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

    // Add faces
    if (json.faces) {
      const indices = new Uint32Array(json.faces.flat());
      geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    }

    // Add colors if present
    if (json.colors) {
      const colors = new Float32Array(json.colors.flat());
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    }

    geometry.computeVertexNormals();

    return {
      type: 'mesh',
      geometry: geometry,
      vertices: json.vertices,
      colors: json.colors,
      metadata: {
        format: 'JSON Mesh',
        fileName: file.name,
        fileSize: file.size,
        vertexCount: json.vertices.length,
        faceCount: json.faces ? json.faces.length : 0
      }
    };
  }

  // Parse points JSON format
  parsePointsJSON(json, file) {
    const geometry = new THREE.BufferGeometry();

    // Add points
    const points = new Float32Array(json.points.flat());
    geometry.setAttribute('position', new THREE.BufferAttribute(points, 3));

    // Add colors if present
    if (json.colors) {
      const colors = new Float32Array(json.colors.flat());
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    }

    return {
      type: 'points',
      geometry: geometry,
      vertices: json.points,
      colors: json.colors,
      scalars: json.scalars,
      metadata: {
        format: 'JSON Points',
        fileName: file.name,
        fileSize: file.size,
        pointCount: json.points.length
      }
    };
  }

  // Extract vertices from geometry
  extractVertices(geometry) {
    const positions = geometry.attributes.position.array;
    const vertices = [];

    for (let i = 0; i < positions.length; i += 3) {
      vertices.push([positions[i], positions[i+1], positions[i+2]]);
    }

    return vertices;
  }

  // Extract colors from geometry
  extractColors(geometry) {
    if (!geometry.attributes.color) return null;

    const colorAttrib = geometry.attributes.color.array;
    const colors = [];

    for (let i = 0; i < colorAttrib.length; i += 3) {
      colors.push([colorAttrib[i], colorAttrib[i+1], colorAttrib[i+2]]);
    }

    return colors;
  }

  // Load from URL
  async loadFromURL(url) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type');
    const extension = url.split('.').pop().toLowerCase();

    if (contentType.includes('json') || extension === 'json') {
      const json = await response.json();
      return this.parseSimulationData(json, { name: url.split('/').pop(), size: 0 });
    } else {
      const blob = await response.blob();
      const file = new File([blob], url.split('/').pop());
      return this.loadFile(file);
    }
  }

  // Load simulation data from WebSocket
  parseWebSocketData(data) {
    if (typeof data === 'string') {
      data = JSON.parse(data);
    }

    return {
      type: 'simulation',
      grid: data.grid,
      fields: data.fields || {},
      timestep: data.timestep || 0,
      metadata: {
        format: 'WebSocket Stream',
        timestamp: Date.now(),
        ...data.metadata
      }
    };
  }
}

// Export
window.DataLoader = DataLoader;
